import React from 'react';

import { observer } from 'mobx-react';
import { useForm } from 'react-hook-form';
import { InputAdornment } from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';

import { LbInput } from '@infoods/lifebase-ui';
import classes from './InvoiceDetailsModal.module.scss';
import { useInvoiceCustomers } from './hooks/useInvoiceCustomers';
import { LbBigModal } from '../../../../../../shared/components/modals';
import { pluralize } from '../../../../../../shared/utils/stringUtils';
import { HeaderRow } from './HeaderRow/HeaderRow';
import { useSort } from '../../../../../../shared/hooks/useSort';

import {
  GridTableCell,
  GridTableRow,
} from '../../../../../../shared/components/table-components';
import { StatusCell } from './StatusCell/StatusCell';
import { useAutoSubmitForm } from '../../../../../../shared/hooks/useAutoSubmitForm';
import { EmptyList } from '../../../../../../shared/components/ui-components';
import { apiStatusStore } from '../../../../../../shared/stores/apiStatusStore';

type InvoiceDetailsModalProps = {
  entityId: number;
  invoiceId: number;
  onClose: () => void;
};

type InvoiceCustomerFormValues = {
  searchTerm: string;
};

const InvoiceDetailsModal = observer(
  ({ entityId, invoiceId, onClose }: InvoiceDetailsModalProps) => {
    const { control, watch, handleSubmit } = useForm<InvoiceCustomerFormValues>();

    const {
      filteredInvoiceCustomers,
      activeCustomers,
      canceledCustomers,
      isSearch,
      applySearchFilter,
    } = useInvoiceCustomers({ entityId, invoiceId });

    const { isLoading } = apiStatusStore;

    const { sortedData, sortMethods } = useSort({
      data: filteredInvoiceCustomers,
      sortTypeConfig: { fullName: 'STRING', status: 'STRING' },
      defaultSortInfo: { fieldName: 'fullName', direction: 'ASC' },
    });

    useAutoSubmitForm(
      handleSubmit((data) => {
        applySearchFilter(data.searchTerm);
      }),
      watch,
      ['searchTerm'],
    );

    const description = (
      <div className="private-info">
        {pluralize(activeCustomers, 'Active paid customer')},&nbsp;
        {pluralize(canceledCustomers, 'Canceled paid customer')}
      </div>
    );

    return (
      <LbBigModal
        open
        title="Customers list"
        isLoading={isLoading}
        onClose={onClose}
        description={description}
        buttonOptions={{
          noHandler: onClose,
          noText: 'Close',
          yesVisible: false,
        }}
      >
        <div className={classes.customersTable}>
          <LbInput
            control={control}
            variant="outlined"
            size="small"
            name="searchTerm"
            className={classes.searchInput}
            placeholder="Search"
            selectValueOnFocus
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchRoundedIcon fontSize="large" color="tertiary" />
                </InputAdornment>
              ),
            }}
          />
          <HeaderRow sortMethods={sortMethods} />
          {sortedData.map((customer) => (
            <GridTableRow
              key={customer.customerId}
              className={classes.customerRow}
              variant="unstyled"
            >
              <GridTableCell className="private-info">{customer.fullName}</GridTableCell>
              <StatusCell
                status={customer.status}
                accessTillDate={customer.accessTillDate}
              />
            </GridTableRow>
          ))}
        </div>
        <EmptyList
          show={!sortedData.length}
          isSearch={isSearch}
          description="There are no customers"
        />
      </LbBigModal>
    );
  },
);

export { InvoiceDetailsModal };
